import { useCallback, useState } from 'react';

import { Timestamp } from 'firebase/firestore';

import { useEventScoreMutator } from '../dataMutators/useEventScoreMutator';
import Event from '../types/Event';
import { EVENT_SCORE_STATUSES } from '../types/EventScore';

export interface GhinIndexDateChange {
  /** null = nothing waiting; undefined = clearing the date is waiting for confirmation. */
  pendingDate: Timestamp | undefined | null;
  isProcessing: boolean;
  /** Applies the new date at once, or holds it for confirmation when any day's scores are published. */
  requestChange: (date: Timestamp | undefined) => void;
  /** Applies the held date; with `unpublish`, also unpublishes every day whose scores are published. */
  applyPending: (unpublish: boolean) => Promise<void>;
  cancelPending: () => void;
}

/**
 * Changing an event's Handicap Index date: marks it as set by hand and asks the daily index fetch
 * to refresh to the new date. Once any day's scores are published the change could move results, so
 * it waits for the admin to confirm (see GhinIndexDateChangeConfirm). Shared by the event editor's
 * finalize step and the tee sheet settings.
 */
export default function useGhinIndexDateChange(
  event: Pick<Event, 'orgId' | 'itemId' | 'leagueId' | 'days'>,
  onUpdate: (updates: Partial<Event>) => void,
): GhinIndexDateChange {
  const { unpublishEventScores } = useEventScoreMutator();
  const [pendingDate, setPendingDate] = useState<Timestamp | undefined | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const hasPublishedScores = event.days.some((d) => d.scoreStatus === EVENT_SCORE_STATUSES.PUBLISHED);

  const apply = useCallback((date: Timestamp | undefined) => {
    onUpdate({
      ghinIndexDate: date,
      ghinIndexDateManuallySet: true,
      ...(date ? { ghinUpdateRequested: date } : {}),
    });
  }, [onUpdate]);

  const requestChange = useCallback((date: Timestamp | undefined) => {
    if (hasPublishedScores) setPendingDate(date);
    else apply(date);
  }, [hasPublishedScores, apply]);

  const applyPending = useCallback(async (unpublish: boolean) => {
    if (pendingDate === null) return;
    setIsProcessing(true);
    apply(pendingDate);
    if (unpublish) {
      const publishedDayIndices = event.days
        .map((d, i) => ({ d, i }))
        .filter(({ d }) => d.scoreStatus === EVENT_SCORE_STATUSES.PUBLISHED)
        .map(({ i }) => i);
      await Promise.all(
        publishedDayIndices.map((i) =>
          unpublishEventScores(event.orgId, event.itemId!, event.leagueId, i, event.days)
        )
      );
    }
    setPendingDate(null);
    setIsProcessing(false);
  }, [pendingDate, apply, unpublishEventScores, event]);

  const cancelPending = useCallback(() => setPendingDate(null), []);

  return { pendingDate, isProcessing, requestChange, applyPending, cancelPending };
}
